import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { IImportsResponse } from 'APIPackage/ImportExportModule/interfaces/IImportsResponse';
import {
  fetchImportCreate,
  fetchImportDelete,
  fetchImportFileUpload,
  fetchImportFileUrl,
  fetchImports,
  fetchImportsByPlan,
  fetchWarningsFileUrl,
} from 'APIPackage/ImportExportModule/services/importsApi';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import {
  IMPORT_GET_ERROR,
  IMPORT_TEMPLATE_CREATE_ERROR,
  IMPORT_TEMPLATE_CREATE_ERROR_NOT_FOUND,
  IMPORT_TEMPLATE_CREATE_SUCCESSFULLY,
  KNM_PLAN_NOT_AVAILABLE_TO_ADJUSTMENT,
} from 'CorePackage/MainModule/constants/notices';
import { IImportTemplate } from 'APIPackage/ImportExportModule/interfaces/IImportTemplate';
import { ImportFileStatusEnum } from 'APIPackage/ImportExportModule/enums/ImportFileStatusEnum';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { ROUTE_TEMPLATES_IMPORT } from 'CorePackage/MainModule/routes/routes';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import {
  dictionariesFederalLawsGet,
  dictionariesFilteredFederalLawsGet,
  dictionariesSupervisionTypesItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { getFilteredFederalLawsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import {
  catalogGet,
  catalogKnmSupervisionTypesByKnmTypeAndSubjectTypeGet,
} from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { exportSimilaritiesFromImportByPlanId } from 'APIPackage/SimilarityModule/services/similaritiesApi';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getAccountKnoOrganizationsSelectOptions } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';

import {
  importCreateTemplateWithFile,
  importDownloadFileByTemplateId,
  importDownloadWarningsFile,
  importExportSimilaritiesListByPlanId,
  importGetData,
  importGetTemplate,
  importRemove,
  importSetData,
  importSetKnmTypeId,
  importSetKnoOrganizationErvkId,
  importSetKnoOrganizationId,
  importSetSubjectTypeErvkId,
  importSetSubjectTypeId,
  importSetSupervisionTypeErvkId,
  importSetTemplate,
  importUploadFile,
} from '../actions/importActions';
import {
  getImportSearchValue,
  getImportOrder,
  getImportData,
  getImportPage,
  getImportCurrentUser,
  getImportFilters,
  getImportKnoOrganizationErvkId,
  getImportPlanTypeFederalLawErvkId,
  getImportFederalLawErvkId,
  getImportSubjectTypeId,
  getImportKnmTypeId,
} from '../selectors/importSelectors';
import { ImportActionsEnum } from '../enums/ImportActionsEnum';
import { IImportCreateTemplateWithFile } from '../interfaces/IImportCreateTemplateWithFile';
import { ImportNotification } from '../components/ImportNotification';
import { mapDataToImportRow } from '../common/mapDataToImportRow';

const PAGE_SIZE = 50;
const IMPORT_DATA_REFRESH_DELAY = 1000;

export function* watchImportSaga() {
  yield takeEvery(ImportActionsEnum.IMPORT_INIT_PAGE, importInitPageSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_GET_DATA, importGetDataSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_GET_DATA_NEXT_PAGE, importGetDataNextPageSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_REMOVE, importRemoveSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_UPLOAD_FILE, importUploadFileSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_CREATE_TEMPLATE_WITH_FILE, importCreateTemplateWithFileSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_DOWNLOAD_FILE_BY_TEMPLATE_ID, importDownloadFileByTemplateIdSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_DOWNLOAD_WARNINGS_FILE, importDownloadWarningsFileSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_GET_TEMPLATE, importGetTemplateSaga);
  yield takeEvery(ImportActionsEnum.IMPORT_SET_KNO_ORGANIZATION_ERVK_ID, importSetKnoOrganizationErvkIdSaga);
  yield takeEvery(
    ImportActionsEnum.IMPORT_EXPORT_SIMILARITIES_LIST_BY_PLAN_ID,
    importExportSimilaritiesListByPlanIdSaga,
  );
  yield takeEvery(
    [ImportActionsEnum.IMPORT_SET_PLAN_TYPE, ImportActionsEnum.IMPORT_SET_FEDERAL_LAW],
    importGetSupervisionTypesByFederalLawSaga,
  );
  yield takeEvery(
    DictionariesActionsEnum.DICTIONARIES_FILTERED_FEDERAL_LAWS_GET_SUCCESS,
    importGetSupervisionTypesExcluding248Saga,
  );
  yield takeEvery(
    [ImportActionsEnum.IMPORT_SET_KNM_TYPE_ID, ImportActionsEnum.IMPORT_SET_SUBJECT_TYPE_ID],
    importGetKnmSupervisionTypesByKnmTypeAndSubjectTypeSaga,
  );
}

function* importInitPageSaga() {
  yield put(importGetData());
  yield put(dictionariesFederalLawsGet());
  yield put(catalogGet(CommonCatalogsNamesEnum.KNM_PLAN_TYPES));
}

function* importGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchValue = getImportSearchValue(state);
    const order = getImportOrder(state);
    const isCurrentUser = getImportCurrentUser(state);
    const filters = getImportFilters(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

    const res: IImportsResponse = yield fetchImports({
      page: '0',
      size: PAGE_SIZE.toString(),
      search: searchValue,
      order: order ?? '',
      currentUser: isCurrentUser,
      fileStatus: filters.statuses,
      fileType: filters.fileTypes,
    });

    yield put(importSetData(mapDataToImportRow(res.list, isErknm), res.page, res.pageCount, res.totalCount));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: IMPORT_GET_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.GET_TEMPLATES,
        }),
      );
    },
  });
}

function* importGetDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const searchValue = getImportSearchValue(state);
    const data = getImportData(state);
    const page = getImportPage(state);
    const order = getImportOrder(state);
    const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

    const res: IImportsResponse = yield fetchImports({
      page: (page + 1).toString(),
      size: PAGE_SIZE.toString(),
      search: searchValue,
      order: order ?? '',
    });

    yield put(
      importSetData([...data, ...mapDataToImportRow(res.list, isErknm)], res.page, res.pageCount, res.totalCount),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importRemoveSaga(action: ActionType<typeof importRemove>) {
  function* funcTry() {
    const { removedIds } = action.payload;

    yield removedIds.forEach(id => {
      fetchImportDelete({
        id,
      });
    });
    yield delay(IMPORT_DATA_REFRESH_DELAY);
    yield put(importGetData());
    if (removedIds.length > 1) {
      const deletingQuantityText = num2str(removedIds.length, ['запись', 'записи', 'записей']);

      yield put(
        addNoticeAction({
          renderContent: `Успешно ${num2str(removedIds.length, ['удалена', 'удалено', 'удалено'])} ${
            removedIds.length
          } ${deletingQuantityText}`,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.REMOVE_NOTES,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: 'Успешно удалена 1 запись',
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.REMOVE_NOTES,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importUploadFileSaga(action: ActionType<typeof importUploadFile>) {
  function* funcTry() {
    const { id, file } = action.payload;

    yield fetchImportFileUpload({
      id,
      file,
    });
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importCreateTemplateWithFileSaga(action: ActionType<typeof importCreateTemplateWithFile>) {
  function* funcTry() {
    const { values, planId } = action.payload;
    const lawInterface: LawInterfacesEnum = yield select(getLawInterface);
    const isErknm = lawInterface === LawInterfacesEnum.FZ_248;
    const prosecutorOrganizationId = values?.prosecutorOrganization?.id;
    const knoOrganizationId = values?.knoOrganization?.idStr || values?.knoOrganization?.id;
    const knoOrganizationFunctionId = values?.knoOrganizationFunction?.id;
    const subjectTypeId = values?.subjectType?.id;
    const supervisionTypeId = values?.supervisionType?.id;
    const knmTypeId = values?.knmType?.id;
    const subjectTypeErvkValue = values?.subjectTypeErvk?.value;
    const supervisionTypeErvkValue = values.supervisionTypeErvk?.value;
    const planType = values.planTypeWithFederalLaw?.planTypeId;

    const templateData: IImportCreateTemplateWithFile = {
      ...(prosecutorOrganizationId && {
        ervkProsecutorOrganization: { ervkProsecutorOrganizationID: prosecutorOrganizationId },
      }),
      ...(knoOrganizationId && { knoOrganization: { id: knoOrganizationId } }),
      ...(typeof knoOrganizationFunctionId === 'number' && {
        knoOrganizationFunction: { id: knoOrganizationFunctionId },
      }),
      ...(typeof subjectTypeId === 'number' && { subjectType: { id: subjectTypeId } }),
      ...(typeof supervisionTypeId === 'number' && { supervisionType: { id: supervisionTypeId } }),
      fileType: values?.fileType?.value,
      file: values?.file,
      importedCount: 0,
      approval: values?.approval,
      name: values?.name,
      planId: values?.planId || undefined,
      ...(typeof knmTypeId === 'number' && { knmType: { id: knmTypeId } }),
      erpId: values.erpId ?? null,
      isFederalLaw248: isErknm,
      ...(subjectTypeErvkValue && {
        subjectTypeErvk: subjectTypeErvkValue,
      }),
      ...(supervisionTypeErvkValue && {
        supervisionTypeErvk: supervisionTypeErvkValue,
      }),
      ...(planType && {
        knmPlanType: String(planType),
      }),
      ...(values?.federalLaw && {
        ervkFederalLaw: {
          ervkId: values.federalLaw.id,
        },
      }),
    };

    const createTemplateResponse: IImportTemplate | undefined = yield fetchImportCreate({
      data: {
        ...templateData,
        planId,
        fileStatus: ImportFileStatusEnum.NEW,
      },
    });
    const importId = createTemplateResponse?.id;
    const uploadId = createTemplateResponse?.uploadId;

    if (values?.file && importId) {
      yield fetchImportFileUpload({
        id: importId,
        file: values?.file,
      });
    }
    yield delay(IMPORT_DATA_REFRESH_DELAY);
    yield put(importGetData());
    yield put(
      addNoticeAction({
        renderContent: (
          <ImportNotification
            importId={uploadId}
            message={IMPORT_TEMPLATE_CREATE_SUCCESSFULLY}
            routeTo={ROUTE_TEMPLATES_IMPORT}
          />
        ),
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.IMPORT_TEMPLATE,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: error?.message?.includes('404')
            ? IMPORT_TEMPLATE_CREATE_ERROR_NOT_FOUND
            : IMPORT_TEMPLATE_CREATE_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.CREATE_TEMPLATE,
        }),
      );
    },
  });
}

function* importDownloadFileByTemplateIdSaga(action: ActionType<typeof importDownloadFileByTemplateId>) {
  function* funcTry() {
    const { id, fileName } = action.payload;
    const url: string | undefined = yield fetchImportFileUrl({
      id,
    });

    if (url) {
      saveFile(url, fileName);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importDownloadWarningsFileSaga(action: ActionType<typeof importDownloadWarningsFile>) {
  function* funcTry() {
    const { id, fileName } = action.payload;
    const url: string | undefined = yield fetchWarningsFileUrl(id);

    if (url) {
      saveFile(url, fileName);
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importGetTemplateSaga(action: ActionType<typeof importGetTemplate>) {
  function* funcTry() {
    const { planId } = action.payload;
    const response: IImportTemplate = yield fetchImportsByPlan({
      planId,
    });

    const knoOrganizationId = response?.knoOrganization?.id;
    const userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[] = yield select(
      getAccountKnoOrganizationsSelectOptions,
    );
    const knoOrganisationSelectOption = userKnoOrganisationsSelectOptions?.find(
      option => option.id === String(knoOrganizationId),
    );
    const knoOrganizationErvkVersionUuid = knoOrganisationSelectOption?.ervkVersionUuid ?? undefined;

    yield put(importSetTemplate(response));
    yield put(importSetKnmTypeId(response?.knmType?.id));
    yield put(importSetSubjectTypeId(response.subjectType?.id));
    yield put(importSetKnoOrganizationId(knoOrganisationSelectOption?.id));
    yield put(importSetKnoOrganizationErvkId(knoOrganizationErvkVersionUuid ?? knoOrganizationId));

    yield put(importSetSupervisionTypeErvkId(response?.supervisionTypeErvk?.dictVersionId ?? undefined));
    yield put(importSetSubjectTypeErvkId(response?.subjectTypeErvk?.dictVersionId ?? undefined));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      if (err?.message?.includes('KNM_PLAN_NOT_AVAILABLE_TO_ADJUSTMENT')) {
        yield put(
          addNoticeAction({
            renderContent: KNM_PLAN_NOT_AVAILABLE_TO_ADJUSTMENT,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.KNM_PLAN_NOT_AVAILABLE_TO_ADJUSTMENT,
          }),
        );
      }
    },
  });
}

function* importSetKnoOrganizationErvkIdSaga(action: ActionType<typeof importSetKnoOrganizationErvkId>) {
  const { knoOrganizationErvkId } = action.payload;

  if (knoOrganizationErvkId) {
    yield put(dictionariesSupervisionTypesItemGet([knoOrganizationErvkId], true));
    yield put(dictionariesFilteredFederalLawsGet(knoOrganizationErvkId));
  }
}

function* importExportSimilaritiesListByPlanIdSaga(action: ActionType<typeof importExportSimilaritiesListByPlanId>) {
  function* funcTry() {
    const { format, id, type } = action.payload;
    const res: IExport = yield exportSimilaritiesFromImportByPlanId(id, format, type);

    if (res && res.id) {
      yield put(addNoticeImportNotification(res.id));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* importGetSupervisionTypesByFederalLawSaga() {
  const state: IRootState = yield select();
  const knoOrganizationErvkId = getImportKnoOrganizationErvkId(state);
  const planTypeFederalLawErvkId = getImportPlanTypeFederalLawErvkId(state);
  const federalLawErvkId = getImportFederalLawErvkId(state);

  const resultFederalLaw = planTypeFederalLawErvkId || federalLawErvkId;

  if (knoOrganizationErvkId && resultFederalLaw) {
    yield put(
      dictionariesSupervisionTypesItemGet(
        [knoOrganizationErvkId],
        true,
        resultFederalLaw ? [resultFederalLaw] : undefined,
      ),
    );
  }
}

function* importGetSupervisionTypesExcluding248Saga() {
  const state: IRootState = yield select();
  const knoOrganizationErvkId = getImportKnoOrganizationErvkId(state);

  if (!knoOrganizationErvkId) {
    return;
  }

  const nonFz248Laws = getFilteredFederalLawsSelectOptions(state);
  const nonFz248LawIds = (nonFz248Laws ?? []).map(law => law.value.dictVersionId);

  if (nonFz248LawIds.length > 0) {
    yield put(dictionariesSupervisionTypesItemGet([knoOrganizationErvkId], true, nonFz248LawIds));
  }
}

function* importGetKnmSupervisionTypesByKnmTypeAndSubjectTypeSaga(
  action: ActionType<typeof importSetKnmTypeId> | ActionType<typeof importSetSubjectTypeId>,
) {
  const state: IRootState = yield select();
  const knmTypeId = 'knmTypeId' in action.payload ? action.payload.knmTypeId : getImportKnmTypeId(state);
  const subjectTypeId =
    'subjectTypeId' in action.payload ? action.payload.subjectTypeId : getImportSubjectTypeId(state);

  if (typeof knmTypeId === 'number' && typeof subjectTypeId === 'number') {
    yield put(
      catalogKnmSupervisionTypesByKnmTypeAndSubjectTypeGet({
        knmTypeId,
        subjectTypeId,
      }),
    );
  }
}
